import { inject, Injectable, OnDestroy } from '@angular/core';
import { KeycloakEventTypeLegacy, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

interface ImpersonationClaims {
  impersonator?: {
    id?: string;
    username?: string;
  };
  impersonator_id?: string;
  impersonator_username?: string;
  impersonatorId?: string;
  impersonatorUsername?: string;
  [key: string]: unknown;
}

export interface ImpersonationState {
  active: boolean;
  impersonatorId: string | null;
  impersonatorUsername: string | null;
  impersonatedUserId: string | null;
  impersonatedUsername: string | null;
}

const EMPTY_STATE: ImpersonationState = {
  active: false,
  impersonatorId: null,
  impersonatorUsername: null,
  impersonatedUserId: null,
  impersonatedUsername: null,
};

/**
 * Global helper for detecting Keycloak impersonation sessions.
 *
 * Requires the following Keycloak User Session Note → Token mappers on the client:
 *   - `Impersonator User ID` → claim name `impersonator_id`
 *   - `Impersonator Username` → claim name `impersonator_username`
 *
 * Exposes a reactive `state$` that recomputes on every Keycloak token refresh,
 * plus convenience getters for synchronous checks from components or guards.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService implements OnDestroy {

  private readonly keycloakService = inject(KeycloakService);
  private readonly state$$ = new BehaviorSubject<ImpersonationState>(EMPTY_STATE);
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.refresh();

    this.keycloakService.keycloakEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (
          event.type === KeycloakEventTypeLegacy.OnAuthSuccess ||
          event.type === KeycloakEventTypeLegacy.OnAuthRefreshSuccess ||
          event.type === KeycloakEventTypeLegacy.OnTokenExpired ||
          event.type === KeycloakEventTypeLegacy.OnReady
        ) {
          this.refresh();
        }
        if (event.type === KeycloakEventTypeLegacy.OnAuthLogout) {
          this.state$$.next(EMPTY_STATE);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get state$(): Observable<ImpersonationState> {
    return this.state$$.asObservable();
  }

  get state(): ImpersonationState {
    return this.state$$.value;
  }

  get isImpersonated(): boolean {
    return this.state$$.value.active;
  }

  /**
   * Recompute impersonation state from the current access token claims.
   * Called automatically on Keycloak auth/refresh events.
   */
  refresh(): void {
    const claims = this.readClaims();
    if (!claims) {
      this.state$$.next(EMPTY_STATE);
      return;
    }

    const impersonatorId =
      this.readString(claims.impersonator?.id)
      ?? this.pick(claims, 'impersonator_id', 'impersonatorId');
    const impersonatorUsername =
      this.readString(claims.impersonator?.username)
      ?? this.pick(claims, 'impersonator_username', 'impersonatorUsername');

    if (!impersonatorId && !impersonatorUsername) {
      this.state$$.next(EMPTY_STATE);
      return;
    }

    this.state$$.next({
      active: true,
      impersonatorId,
      impersonatorUsername,
      impersonatedUserId: this.pick(claims, 'sub'),
      impersonatedUsername: this.pick(claims, 'preferred_username', 'email', 'name'),
    });
  }

  private readString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  private pick(claims: ImpersonationClaims, ...keys: string[]): string | null {
    for (const key of keys) {
      const resolved = this.readString(claims[key]);
      if (resolved) {
        return resolved;
      }
    }
    return null;
  }

  private readClaims(): ImpersonationClaims | null {
    try {
      const token = (this.keycloakService.getKeycloakInstance() as unknown as { tokenParsed?: ImpersonationClaims })
        ?.tokenParsed;
      return token ?? null;
    } catch {
      return null;
    }
  }
}
